import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faCheck, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import api from '../Api';

const Notifications = () => {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [perPage] = useState(20);
    const navigate = useNavigate();

    const fetchNotifications = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const response = await api.get(`/notifications?page=${currentPage}&per_page=${perPage}`);
            if (response.data.success) {
                setNotifications(response.data.data.data);
                setTotalPages(response.data.data.last_page);
            }
        } catch (error) {
            console.error('Error fetching notifications:', error);
            setError('Erreur lors du chargement des notifications');
        } finally {
            setLoading(false);
        }
    }, [currentPage, perPage]);

    useEffect(() => {
        fetchNotifications();
    }, [fetchNotifications]);

    const handleNotificationClick = async (notification) => {
        try {
            // Mark as read if not already read
            if (!notification.read_at) {
                await api.post(`/notifications/${notification.id}/mark-read`);
                // Update the notification in the list
                setNotifications(notifications.map(n => 
                    n.id === notification.id ? { ...n, read_at: new Date().toISOString() } : n
                ));
            }
            
            // Navigate to the URL
            if (notification.data.url) {
                navigate(notification.data.url);
            }
        } catch (error) {
            console.error('Error handling notification click:', error);
        }
    };

    const handleMarkAllRead = async () => {
        try {
            const response = await api.post('/notifications/mark-all-read');
            if (response.data.success) {
                // Update all notifications to read
                setNotifications(notifications.map(n => ({ ...n, read_at: new Date().toISOString() })));
            }
        } catch (error) {
            console.error('Error marking all as read:', error);
        }
    };

    const formatTimeAgo = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffInSeconds = Math.floor((now - date) / 1000);
        
        if (diffInSeconds < 60) return 'À l\'instant';
        if (diffInSeconds < 3600) return `Il y a ${Math.floor(diffInSeconds / 60)} min`;
        if (diffInSeconds < 86400) return `Il y a ${Math.floor(diffInSeconds / 3600)} h`;
        if (diffInSeconds < 604800) return `Il y a ${Math.floor(diffInSeconds / 86400)} j`;
        return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
    };

    const formatDateTime = (dateString) => {
        return new Date(dateString).toLocaleString('fr-FR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-4xl mx-auto px-4 py-6">
                {/* Header */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
                    <div className="p-6">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <button
                                    onClick={() => navigate(-1)}
                                    className="mr-4 p-2 text-gray-600 hover:text-gray-900 transition-colors"
                                >
                                    <FontAwesomeIcon icon={faArrowLeft} className="text-xl" />
                                </button>
                                <div>
                                    <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                                        <FontAwesomeIcon icon={faBell} className="mr-3 text-red-600" />
                                        Notifications
                                    </h1>
                                    <p className="text-gray-600 mt-1">
                                        {notifications.length} notification{notifications.length !== 1 ? 's' : ''}
                                    </p>
                                </div>
                            </div>
                            {notifications.some(n => !n.read_at) && (
                                <button
                                    onClick={handleMarkAllRead}
                                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center"
                                >
                                    <FontAwesomeIcon icon={faCheck} className="mr-2" />
                                    Tout marquer comme lu
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                    {loading ? (
                        <div className="p-12 text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
                            <p className="text-gray-500 mt-4">Chargement des notifications...</p>
                        </div>
                    ) : error ? (
                        <div className="p-12 text-center">
                            <div className="text-red-500 mb-4">
                                <FontAwesomeIcon icon={faBell} className="text-4xl" />
                            </div>
                            <p className="text-red-600 font-medium">{error}</p>
                            <button
                                onClick={fetchNotifications}
                                className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                            >
                                Réessayer
                            </button>
                        </div>
                    ) : notifications.length === 0 ? (
                        <div className="p-12 text-center">
                            <FontAwesomeIcon icon={faBell} className="text-6xl text-gray-300 mb-4" />
                            <h3 className="text-xl font-semibold text-gray-900 mb-2">Aucune notification</h3>
                            <p className="text-gray-500">Vous êtes à jour ! Toutes vos notifications apparaîtront ici.</p>
                        </div>
                    ) : (
                        <div>
                            {/* Notifications List */}
                            <div className="divide-y divide-gray-200">
                                {notifications.map((notification) => (
                                    <div
                                        key={notification.id}
                                        onClick={() => handleNotificationClick(notification)}
                                        className={`p-6 cursor-pointer hover:bg-gray-50 transition-colors ${
                                            !notification.read_at ? 'bg-blue-50' : ''
                                        }`}
                                    >
                                        <div className="flex items-start space-x-4">
                                            <div className={`flex-shrink-0 w-3 h-3 rounded-full mt-2 ${
                                                !notification.read_at ? 'bg-red-600' : 'bg-gray-300'
                                            }`}></div>
                                            <div className="flex-1 min-w-0">
                                                <p className={`text-base ${!notification.read_at ? 'font-semibold text-gray-900' : 'text-gray-700'} leading-relaxed`}>
                                                    {notification.data.message}
                                                </p>
                                                {notification.data.batch_role && (
                                                    <div className="mt-3 flex flex-wrap gap-2">
                                                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                                                            {notification.data.batch_role.toUpperCase()}
                                                        </span>
                                                        {notification.data.batch_amount != null && (
                                                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                                                                {Number(notification.data.batch_amount).toLocaleString('fr-FR', {
                                                                    style: 'currency',
                                                                    currency: 'MAD'
                                                                })}
                                                            </span>
                                                        )}
                                                        {Number(notification.data.batch_credit_applied || 0) > 0 && (
                                                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                                                                {Number(notification.data.batch_total_net).toLocaleString('fr-FR')} total − {Number(notification.data.batch_credit_applied).toLocaleString('fr-FR')} déjà en caisse
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                                <div className="mt-3 flex items-center justify-between">
                                                    <p className="text-sm text-gray-500">
                                                        {formatTimeAgo(notification.created_at)}
                                                    </p>
                                                    <p className="text-xs text-gray-400">
                                                        {formatDateTime(notification.created_at)}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="px-6 py-4 border-t border-gray-200">
                                    <div className="flex items-center justify-between">
                                        <p className="text-sm text-gray-700">
                                            Page {currentPage} sur {totalPages}
                                        </p>
                                        <div className="flex space-x-2">
                                            <button
                                                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                                disabled={currentPage === 1}
                                                className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                Précédent
                                            </button>
                                            <button
                                                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                                                disabled={currentPage === totalPages}
                                                className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                Suivant
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Notifications;
